/**
 * Dialog de configuração para o node Link de Agendamento Online
 * Permite selecionar tipo de evento, origem do deal e variável de saída
 * 
 * O link gerado é idêntico ao do modal de negócio:
 * https://uaizychat.lovable.app/schedule/{tenant_slug}/{event_slug}?deal_id={deal_id}
 * 
 * RELEASE: 1.1.0 - 2026-09-07 - Variável de saída migrada para o modal de destino.
 * RELEASE: 1.0.0 - 2026-02-18 - Novo dialog de configuração
 */

import { useState, useEffect, useRef } from 'react';
import { stripTemplateVariables } from '@/lib/schedulingUtils';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { CalendarClock, Link, AlertCircle } from 'lucide-react';
import { TenantVariable } from '@/hooks/useTenantVariables';
import { FlowNode, CrmSchedulingLinkConfig } from '@/types/flows';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { showToast } from '@/lib/toast';
import { NodeIdField, NodeIdFieldRef } from '../NodeIdField';
import { VariablePickerCombobox } from '../VariablePickerCombobox';
import { VariableDestinationPicker } from '../VariableDestinationPicker';

interface ConfigureCrmSchedulingLinkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  node: FlowNode;
  onSave: (nodeId: string, config: CrmSchedulingLinkConfig) => void;
}

/**
 * Interface mínima de um tipo de evento de agendamento
 */
interface SchedulingEventType {
  id: string;
  slug: string;
  title: string;
  color?: string | null;
  duration_minutes?: number | null;
  is_active: boolean;
}

/**
 * Dialog para configurar geração de link de agendamento online
 * Três seções: tipo de evento → origem do deal → variável de saída
 */
export function ConfigureCrmSchedulingLinkDialog({
  open,
  onOpenChange,
  node,
  onSave,
}: ConfigureCrmSchedulingLinkDialogProps) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id || '';
  /** Ref para capturar apelido do node */
  const nicknameRef = useRef<NodeIdFieldRef>(null);

  // Estado do formulário baseado na configuração atual do node
  const existingConfig = node.data.config as CrmSchedulingLinkConfig | undefined;

  const [selectedEventTypeId, setSelectedEventTypeId] = useState(existingConfig?.event_type_id || '');
  const [dealSelection, setDealSelection] = useState<'most_recent' | 'from_variable' | 'from_trigger'>(
    existingConfig?.deal_selection || 'from_trigger'
  );
  const [dealVariableName, setDealVariableName] = useState(existingConfig?.deal_variable_name || '');
  const [outputVariableId, setOutputVariableId] = useState(existingConfig?.output_variable_id || '');
  const [outputVariableName, setOutputVariableName] = useState(existingConfig?.output_variable || '');

  /**
   * Busca tipos de evento de agendamento ativos do tenant
   */
  const { data: eventTypes = [], isLoading: isLoadingEvents } = useQuery({
    queryKey: ['scheduling_event_types_active', tenantId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_scheduling_event_types', {
        p_active_only: true,
        p_tenant_hint: tenantId,
      });

      if (error) {
        showToast.error('Erro ao carregar tipos de evento', error.message);
        return [] as SchedulingEventType[];
      }

      return (data || []) as SchedulingEventType[];
    },
    enabled: open && !!tenantId,
  });

  // Evento selecionado completo (para salvar slug, título, cor e duração)
  const selectedEvent = eventTypes.find(et => et.id === selectedEventTypeId);

  // Sincroniza estado ao reabrir dialog para node diferente
  useEffect(() => {
    if (open) {
      const cfg = node.data.config as CrmSchedulingLinkConfig | undefined;
      setSelectedEventTypeId(cfg?.event_type_id || '');
      setDealSelection(cfg?.deal_selection || 'from_trigger');
      setDealVariableName(cfg?.deal_variable_name || '');
      setOutputVariableId(cfg?.output_variable_id || '');
      setOutputVariableName(cfg?.output_variable || '');
    }
  }, [open, node.id]);

  /**
   * Valida e salva a configuração do node
   */
  const handleSave = () => {
    if (!selectedEvent) {
      showToast.error('Configuração incompleta', 'Selecione um tipo de evento de agendamento');
      return;
    }

    if (!outputVariableName.trim()) {
      showToast.error('Configuração incompleta', 'Informe o nome da variável de saída');
      return;
    }

    if (dealSelection === 'from_variable' && !dealVariableName.trim()) {
      showToast.error('Configuração incompleta', 'Selecione a variável que contém o ID do negócio');
      return;
    }

    const config: CrmSchedulingLinkConfig = {
      event_type_id: selectedEvent.id,
      event_type_slug: selectedEvent.slug,
      event_type_title: selectedEvent.title,
      event_type_color: selectedEvent.color || undefined,
      event_type_duration_minutes: selectedEvent.duration_minutes || undefined,
      deal_selection: dealSelection,
      deal_variable_name: dealSelection === 'from_variable' ? dealVariableName.trim() : undefined,
      output_variable: outputVariableName,
      output_variable_id: outputVariableId || undefined,
    };

    onSave(node.id, { ...config, nickname: nicknameRef.current?.getNickname() } as any);
    onOpenChange(false);
    showToast.success('Node configurado', 'Link de agendamento configurado com sucesso');
  };

  // Habilita salvar apenas quando os campos obrigatórios estão preenchidos
  // Habilita salvar apenas quando os campos obrigatórios estão preenchidos
  const canSave = !!selectedEventTypeId && !!outputVariableName.trim() && (
    dealSelection !== 'from_variable' || !!dealVariableName.trim()
  );

  // Preview do link que será gerado (para ilustração)
  // Usa window.location.origin para ser agnóstico ao ambiente (preview vs produção)
  const previewLink = selectedEvent
    ? `${window.location.origin}/schedule/${currentTenant?.slug || '{tenant}'}/${selectedEvent.slug}?deal_id={deal_id}`
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-cyan-500/10">
              <CalendarClock className="h-5 w-5 text-cyan-500" />
            </div>
            <div>
              <DialogTitle>Link de Agendamento Online</DialogTitle>
              <DialogDescription>
                Gera um link único de agendamento vinculado ao negócio
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* ID do node */}
          <NodeIdField ref={nicknameRef} nodeId={node.id} defaultNickname={node.data.config?.nickname} />

          <Separator />

          {/* =============================================
              SEÇÃO 1 — TIPO DE EVENTO
          ============================================= */}
          <div className="space-y-3">
            <Label className="text-sm font-semibold flex items-center gap-2">
              <CalendarClock className="h-4 w-4 text-cyan-500" />
              Tipo de Evento
            </Label>

            {isLoadingEvents ? (
              <p className="text-sm text-muted-foreground py-2">Carregando tipos de evento...</p>
            ) : eventTypes.length === 0 ? (
              /* Estado vazio: sem tipos de evento configurados */
              <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-2">
                <div className="flex items-start gap-2 text-amber-600">
                  <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <p className="text-sm">
                    Nenhum tipo de evento de agendamento ativo encontrado.
                    Configure os tipos de evento em{' '}
                    <span className="font-medium">Configurações → Agendamento</span>.
                  </p>
                </div>
              </div>
            ) : (
              /* Lista de tipos de evento como cards selecionáveis */
              <div className="space-y-2">
                {eventTypes.map((et) => (
                  <button
                    key={et.id}
                    type="button"
                    onClick={() => setSelectedEventTypeId(et.id)}
                    className={`
                      w-full flex items-center gap-3 p-3 rounded-lg border-2 text-left
                      transition-all duration-150
                      ${selectedEventTypeId === et.id
                        ? 'border-cyan-500 bg-cyan-500/5'
                        : 'border-border hover:border-cyan-400 hover:bg-muted/50'
                      }
                    `}
                  >
                    {/* Dot colorido do evento */}
                    <span
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: et.color || '#06b6d4' }}
                    />
                    {/* Título e duração */}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{stripTemplateVariables(et.title)}</p>
                    </div>
                    {/* Duração em minutos */}
                    {et.duration_minutes && (
                      <span className="text-xs text-muted-foreground flex-shrink-0">
                        {et.duration_minutes} min
                      </span>
                    )}
                    {/* Indicador de selecionado */}
                    {selectedEventTypeId === et.id && (
                      <span className="w-2 h-2 rounded-full bg-cyan-500 flex-shrink-0" />
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* Preview do link gerado */}
            {previewLink && (
              <div className="rounded-md bg-muted/50 border border-border p-2">
                <p className="text-[10px] text-muted-foreground mb-1 flex items-center gap-1">
                  <Link className="h-3 w-3" />
                  Formato do link gerado:
                </p>
                <p className="text-[10px] font-mono break-all text-foreground/80">
                  {previewLink}
                </p>
              </div>
            )}
          </div>

          <Separator />

          {/* =============================================
              SEÇÃO 2 — ORIGEM DO DEAL
          ============================================= */}
          <div className="space-y-3">
            <Label className="text-sm font-semibold">Origem do Negócio</Label>
            <p className="text-xs text-muted-foreground -mt-1">
              De onde o ID do negócio será obtido para compor o link
            </p>

            <Select
              value={dealSelection}
              onValueChange={(v) => setDealSelection(v as typeof dealSelection)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="from_trigger">
                  Do negócio no gatilho CRM (padrão)
                </SelectItem>
                <SelectItem value="most_recent">
                  Negócio mais recente do contato
                </SelectItem>
                <SelectItem value="from_variable">
                  De uma variável
                </SelectItem>
              </SelectContent>
            </Select>

            {/* Seletor de variável — exibido apenas quando from_variable */}
            {dealSelection === 'from_variable' && (
              <div className="space-y-1.5 pl-1">
                <Label className="text-xs text-muted-foreground">
                  Variável com o ID do negócio
                </Label>
                <VariablePickerCombobox
                  tenantId={tenantId}
                  value={dealVariableName}
                  onChange={(variable: TenantVariable | null) => setDealVariableName(variable?.name || '')}
                  placeholder="Selecionar variável com deal_id..."
                />
              </div>
            )}
          </div>

          <Separator />

          {/* =============================================
              SEÇÃO 3 — VARIÁVEL DE SAÍDA
          ============================================= */}
          <div className="space-y-3">
            <Label htmlFor="output-variable" className="text-sm font-semibold flex items-center gap-2">
              <Link className="h-4 w-4 text-cyan-500" />
              Variável de Saída
            </Label>
            <p className="text-xs text-muted-foreground">
              O link de agendamento completo será salvo nessa variável
            </p>

            <div className="space-y-1.5">
              {/* Seletor de destino — somente variáveis personalizadas graváveis. */}
              <VariableDestinationPicker
                tenantId={tenantId}
                value={outputVariableId || outputVariableName}
                valueName={outputVariableName}
                onChange={(variable) => {
                  if (variable) {
                    setOutputVariableId(variable.id);
                    setOutputVariableName(variable.name);
                  } else {
                    setOutputVariableId('');
                    setOutputVariableName('');
                  }
                }}
                allowClear
                placeholder="Selecione ou crie uma variável"
              />
              {/* Preview em tempo real da variável */}
              {outputVariableName.trim() && (
                <p className="text-xs text-cyan-600 dark:text-cyan-400 font-mono">
                  {`{{${outputVariableName.trim()}}}`}
                </p>
              )}
            </div>

            <div className="rounded-md bg-muted/40 p-3 text-xs text-muted-foreground space-y-1">
              <p>
                💡 Use{' '}
                <code className="bg-muted px-1 rounded">
                  {outputVariableName.trim()
                    ? `{{${outputVariableName.trim()}}}`
                    : '{{sua_variavel}}'}
                </code>{' '}
                em um node de Texto para enviar o link ao contato.
              </p>
              <p>
                O link gerado é idêntico ao gerado pelo modal do negócio no CRM.
              </p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={!canSave}
            className="bg-cyan-500 hover:bg-cyan-600 text-white"
          >
            <CalendarClock className="h-4 w-4 mr-2" />
            Salvar Configuração
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
